
import {useState, useEffect, useMemo, useRef} from 'react';
import axios from 'axios';
import {APIName} from 'config';


const forbiddenMalwareList = ['nsrl'];


export default function useScanner({managePath, downloadPath, autoScan = false, noDetails = false} = {}) {
  const [data, setData] = useState(null);
  const dataRef = useRef(null);
  const guidRef = useRef();
  const [error, setError] = useState(null);
  const [outOfTime, setOutOfTime] = useState(false);
  const outOfTimeRef = useRef(false);
  const isOutOfTime = () => outOfTimeRef.current && (!dataRef.current || dataRef.current.some(item => item.progress === 'loading'));
  const withTimeCheck = fn => (...args) => {
    if (!isOutOfTime()) {
      fn(...args);
    }
  };

  const scan = () => {
    setError(null);
    return axios.post(`/api/${APIName}/scan`, {managePath, downloadPath})
      .then(({data: {data, timeout = 25}}) => {
        updateData(data);
        setTimeout(() => {
          setOutOfTime(true);
          outOfTimeRef.current = true;
        }, timeout * 1000);
      })
      .catch(e => {
        setError(e);
        throw e;
      });
  };

  const updateData = data => {
    if (!data) {
      dataRef.current = null;
      setData(null);
      return;
    }
    guidRef.current = data.guid;
    const malwareArray = Object.values(data.malware.normal)[0];
    dataRef.current = data.avs
      .filter(avName => !forbiddenMalwareList.includes(avName))
      .map(avName => {
        const isScanned = data.progress[avName] === 100;
        let malwareState = 'unknown';
        let malwareName = '';
        if (isScanned) {
          malwareState = 'clean';
          const item = malwareArray?.find(item => item.avName === avName);
          if (item) {
            malwareState = 'malware';
            malwareName = item.malwareName;
          }
        }
        return {
          avName,
          malwareState,
          malwareName,
          progress: isScanned ? 'scanned' : 'loading',
        };
      });
    setData(dataRef.current);
  };

  useEffect(() => {
    if (!data || data.every(item => item.progress === 'scanned')) {
      return;
    }
    if (noDetails && data.some(item => item.malwareState === 'malware')) {
      return;
    }
    const timeoutId = setTimeout(withTimeCheck(() => {
      axios.get(`/api/${APIName}/scan`, {params: {guid: guidRef.current}})
        .then(withTimeCheck(({data: {data}}) => updateData(data)))
        .catch(e => setError(e));
    }), 3000);
    return () => clearTimeout(timeoutId);
  }, [data]);

  useEffect(() => {
    if (autoScan) {
      scan();
    }
  }, [autoScan]);

  return {scan, data, error, outOfTime: outOfTime && (!data || data.some(item => item.progress === 'loading'))};
}
